import React, {useCallback, useMemo, useRef, useState} from 'react';
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaPlus, FaSearch} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import vehicleNotesService from "../../../../services/web/vehicle-notes-service.js";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import SelectComponent from "../../../../components/select/select-component.jsx";
import platePatternUtils from "../../../../utils/plate-pattern-utils.js";
import {useSearchParams} from "react-router";

const initialFilters = {plate: "", type: "note"}

const VehicleNotesList = () => {
    const navigate = navigateHook();
    const [searchParams] = useSearchParams();

    const [filter, setFilter] = useState({type: "note", filter: []});
    const [state, setState] = useState(initialFilters);

    const {data, isLoading} = useQuery({
        queryFn: () => vehicleNotesService.list(filter),
        queryKey: ["list", {filter}]
    });

    const [columnDefs,] = useState([
        { field: "description", headerName: "Observação", sort: "asc", flex: 1, comparator: aggridUtils.nullLastComparator},
        { field: "plate", headerName: "Placa", width: 300, initialHide: true, valueFormatter: ({value}) => platePatternUtils.formatPlate(value)},
        { field: "qtde", headerName: "Qtde de veículos", width: 150, initialHide: false},
        { field: "dt_create_formated", headerName: "D/H cadastro", flex: 1, comparator: aggridUtils.agGridCompareDateTime},
        { field: "dt_update_formated", headerName: "D/H atualização", flex: 1, comparator: aggridUtils.agGridCompareDateTime},
    ]);

    const gridRef = useRef(undefined);

    const defaultColDef = useMemo(() => ({resizable: false}), []);

    const apply = useCallback(() => {
        setFilter({type: state.type, filter: [{field: "plate", value: platePatternUtils.cleanRawPlate(state.plate)}]});

        gridRef.current.api.applyColumnState({
            state: [
                {colId: 'plate', hide: state.type === "note"},
                {colId: 'qtde', hide: state.type === "plate"}
            ]
        });
    }, [state, gridRef])

    const clear = useCallback(() => {
        setState(initialFilters);
        setFilter({type: "note", filter: []});

        gridRef.current.api.applyColumnState({
            state: [
                {colId: 'plate', hide: true},
                {colId: 'qtde', hide: false}
            ]
        });
    }, []);

    useGlobalKey("Enter", apply);
    useGlobalKey("Escape", clear);

    const onCellClicked = ({data}) => {
        navigate(`/vehicle-notes/form/${data.id}`)
    };

    const onGridReady = ({api}) => {
        if (searchParams.has("plate")) {
            const plate = searchParams.get("plate");
            setState({type: "plate", plate: platePatternUtils.formatPlate(plate)});

            setFilter({type: "plate", filter: [{field: "plate", value: plate}]});

            api.applyColumnState({
                state: [
                    {colId: 'plate', hide: false},
                    {colId: 'qtde', hide: true}
                ]
            });
        }
    }

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Anotações de veículos</Template.Title>

                <Button target={"primary"} onClick={() => navigate("/vehicle-notes/form")}>
                    <FaPlus/> Adicionar anotação
                </Button>
            </Template.Header>

            <Template.Body className={"space-y-2"}>
                <Card>
                    <Card.Body className={"grid grid-cols-12 gap-4"}>
                        <InputForm
                            classNameWrapper={"col-span-6"}
                            id={"plate"}
                            label={"Placa"}
                            value={state.plate}
                            onChange={({target: {value}}) => setState(prev => ({...prev, plate: platePatternUtils.formatPlate(value)}))}
                        />

                        <div className="col-span-6 flex justify-between gap-4">
                            <SelectComponent
                                classNameWrapper={"w-full"}
                                value={state.type}
                                options={[{value:"note", label: "Anotação"}, {value:"plate", label: "Placa"}]}
                                label={"Visualizar por"}
                                onChange={(value) => setState(prev => ({...prev, type: value?.value}))}
                                isClearable={false}
                            />

                            <div className={"flex gap-2"}>
                                <Button target={"default"} onClick={clear} title={"Limpar"}>
                                    <FaEraser />
                                </Button>
                                <Button onClick={apply} title={"Buscar"}>
                                    <FaSearch />
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Body>
                        <div className={"h-[calc(100vh-153px)] aggrid-row-pointer"}>
                            <AgGridReact
                                ref={gridRef}
                                rowData={data ?? []}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                pagination
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                onCellClicked={onCellClicked}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme()}
                                onGridReady={onGridReady}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default VehicleNotesList;